// Comprehensive Utah family law data based on Custody X Change and official Utah sources
const utahData = {
  'utah': {
    name: 'Utah',
    overview: {
      keyPoints: [
        'Utah follows the "best interests of the child" standard with specific factors outlined in Utah Code § 30-3-10.2',
        'Joint legal custody is presumed in Utah unless proven contrary to the child\'s best interests',
        'Utah uses "parent-time" terminology instead of traditional visitation language',
        'The state has detailed statutory parent-time schedules that serve as minimum standards',
        'Utah requires parenting plans in all custody cases involving minor children',
        'The state has specific provisions for relocation with children over 150 miles',
        'Utah courts emphasize maintaining meaningful relationships between children and both parents',
        'The state has mandatory parent education classes for divorcing parents with children'
      ],
      uniqueFeatures: [
        'Utah has detailed statutory parent-time schedules that courts must follow as minimum standards',
        'The state uses "parent-time" instead of visitation, emphasizing both parents\' ongoing roles',
        'Utah has specific relocation statutes requiring court approval for moves over 150 miles',
        'The state allows for parent-time schedules to be modified by agreement without court approval',
        'Utah has provisions for make-up parent-time when scheduled time is missed',
        'The state recognizes step-parent adoption rights under specific circumstances',
        'Utah has specialized domestic violence court procedures with integrated family services',
        'The state allows for electronic monitoring in cases involving domestic violence concerns'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in Utah for at least 3 months before filing, or Utah must be the child\'s home state under UCCJEA',
      filing: 'File in the district court in the county where either parent resides or where the child has lived',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, court order, or presumption of paternity',
      documentation: 'Required documents include petition, summons, financial declaration, and proposed parenting plan in all cases involving children',
      uniqueRules: [
        'Utah requires financial declarations in all family law cases involving children or support',
        'Mandatory parent education classes required within 30 days of filing for divorcing parents',
        'Guardian ad litem appointment is discretionary but common in contested custody cases',
        'Utah allows for temporary orders to be entered without a hearing in emergency situations',
        'The state requires mediation in some judicial districts before contested hearings'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Utah uses "physical custody" to determine where the child primarily resides and who provides day-to-day care',
      legalCustody: 'Legal custody involves decision-making authority for major life decisions including education, healthcare, and religious upbringing',
      jointCustody: 'Joint legal custody is presumed in Utah and allows both parents to make major decisions unless proven contrary to best interests',
      visitation: 'Called "parent-time" - Utah has detailed statutory schedules that serve as minimum standards for non-custodial parents',
      specialProvisions: [
        'Supervised parent-time may be ordered when child safety or welfare concerns exist',
        'Utah has specific statutory parent-time schedules for different age groups of children',
        'Extended parent-time during school breaks and summer vacation is standard',
        'Holiday parent-time follows specific statutory schedules with alternating arrangements',
        'Virtual parent-time (video calls, phone contact) is recognized and protected by statute',
        'Make-up parent-time is required when scheduled time is missed due to the custodial parent\'s actions'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed parent-time schedule including regular rotation, weekends, holidays, and school breaks',
        'Specific holiday and vacation schedules with clear alternating arrangements between parents',
        'Transportation arrangements and responsibility for costs between parents\' residences',
        'Communication methods between parents and guidelines for parent-child contact during parent-time',
        'Decision-making authority allocation for education, healthcare, religious, and extracurricular activities',
        'Dispute resolution procedures including mediation requirements before court intervention',
        'Relocation provisions and notification requirements for moves affecting parent-time schedules',
        'Any restrictions, conditions, or special accommodations for either parent',
        'Child care arrangements during work hours and right of first refusal provisions',
        'Guidelines for introducing new romantic partners to the children',
        'Emergency contact procedures and decision-making protocols'
      ],
      tips: [
        'Utah courts favor detailed, specific parent-time schedules that follow statutory minimums',
        'Include provisions for Utah\'s winter weather and how it affects transportation and exchanges',
        'Address how to handle missed parent-time due to weather, illness, work, or emergencies',
        'Specify meeting locations for exchanges, especially considering Utah\'s geography',
        'Include provisions for the child\'s participation in extracurricular activities and school events',
        'Address how major decisions will be made and communicated between parents',
        'Include technology use guidelines and virtual parent-time provisions',
        'Plan for extended summer parent-time arrangements as provided by Utah statute',
        'Consider Utah\'s unique cultural and religious factors that may affect parenting decisions'
      ]
    },
    childSupport: {
      guidelines: 'Utah uses a modified Income Shares Model based on both parents\' gross income with Utah-specific guidelines and adjustments',
      factors: [
        'Gross monthly income of both parents from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Parent-time with the paying parent (significant time can reduce support obligation)',
        'Standard of living the child would have enjoyed if parents remained together',
        'Special needs of the children requiring additional financial support',
        'Utah\'s cost of living factors and regional variations'
      ],
      calculation: 'Utah Child Support Guidelines using Income Shares Model with state-specific support schedule and adjustments',
      modifications: 'Substantial change in circumstances required, typically 15% change in support amount or significant change in income or parent-time',
      enforcement: 'Strong enforcement through Utah Office of Recovery Services including wage garnishment, asset seizure, tax intercept, and license suspension',
      detailedInfo: {
        basicCalculation: {
          description: 'Utah follows a modified Income Shares Model that estimates what parents would spend on their children if the family were intact, with adjustments for Utah-specific factors.',
          steps: [
            'Calculate each parent\'s gross monthly income from all verifiable sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Calculate adjusted gross monthly income for each parent after allowable deductions',
            'Add both parents\' adjusted gross incomes to determine combined monthly income',
            'Use Utah Child Support Guidelines Table to find basic support obligation for number of children',
            'Allocate support obligation proportionally based on each parent\'s percentage of combined income',
            'Add costs for health insurance, childcare, and extraordinary medical expenses',
            'Apply any adjustments for extended parent-time or special circumstances',
            'Calculate final monthly support amount payable by obligor parent'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Military pay including base pay, allowances, and special pay',
            'Spousal support or maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Royalties, patents, and intellectual property income',
            'Business income from partnerships, corporations, or LLCs'
          ],
          excluded: [
            'Means-tested public assistance (TANF, SNAP, WIC, etc.)',
            'Foster care payments received for non-biological children',
            'Child support received for other children not subject to current order',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or cohabitating partner',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Reimbursement for job-related expenses',
            'Income from illegal activities'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status',
            'State income taxes (Utah state taxes)',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal support/maintenance to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child (added separately to support)',
            'Childcare expenses necessary for employment or education',
            'Extraordinary medical expenses for the child (shared proportionally)'
          ]
        },
        parentTimeAdjustment: {
          description: 'Utah allows adjustment to child support when the paying parent has significant overnight parent-time with the child.',
          thresholds: [
            'No adjustment for less than 25% overnight parent-time (91 nights per year)',
            'Potential adjustment for 25-30% overnight parent-time (91-109 nights per year)',
            'Standard adjustment for 30%+ overnight parent-time (110+ nights per year)',
            'Extended parent-time adjustment may apply for equal or near-equal time arrangements'
          ],
          calculation: 'Adjustment reduces support obligation based on additional direct costs during extended parent-time and reduced costs to custodial parent',
          factors: [
            'Actual overnight stays documented over previous 12 months',
            'Additional housing costs for accommodating child during parent-time',
            'Duplicate expenses (clothing, toys, school supplies) maintained in both homes',
            'Transportation costs for parent-time exchanges',
            'Reduced household expenses for custodial parent during parent-time periods'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically attributable to covering the child',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be reasonable in cost, accessible to child, and provide adequate coverage',
            considerations: [
              'Premium cost must be specifically for child coverage, not family plan differential',
              'Insurance must be available through employer or Utah health marketplace',
              'Coverage should include medical, dental, and vision if reasonably available',
              'Both parents should have access to insurance information and cards'
            ]
          },
          childcare: {
            description: 'Necessary childcare costs directly related to employment, job training, or education',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be work-related or education-related necessity for custodial parent',
            considerations: [
              'Childcare must be necessary for parent to work or attend school',
              'Costs should be reasonable for Utah area and type of care needed',
              'Before and after school care is typically included',
              'Summer childcare during school breaks may be included',
              'Documentation and receipts required for verification'
            ]
          },
          extraordinaryMedical: {
            description: 'Uninsured medical, dental, and vision expenses exceeding $250 per year per child',
            examples: [
              'Dental treatment not covered by insurance (fillings, crowns, extractions)',
              'Orthodontic treatment and appliances',
              'Vision care including eye exams, glasses, and contact lenses',
              'Mental health counseling and therapy sessions',
              'Physical therapy and rehabilitation services',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies (braces, wheelchairs, etc.)',
              'Treatment for chronic conditions requiring ongoing care',
              'Emergency medical treatment and hospitalization costs',
              'Specialized medical care and consultations with specialists'
            ],
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            procedure: 'Parent incurring expense should seek advance approval for non-emergency expenses over $500'
          },
          extracurricular: {
            description: 'Costs for children\'s activities, sports, and enrichment programs',
            examples: [
              'Youth sports team fees, equipment, and travel costs',
              'Music lessons and instrument rental or purchase',
              'Dance, martial arts, gymnastics, or other activity classes',
              'Summer camps and recreational day programs',
              'School trips, field trips, and educational activities',
              'Tutoring and educational support services',
              'Art, theater, or other creative programs',
              'Religious education and youth programs'
            ],
            note: 'Not automatically included in basic support calculation - must be agreed upon by parents or specifically court-ordered',
            considerations: [
              'Activities should be in the child\'s best interest and age-appropriate',
              'Costs should be reasonable and within family\'s financial means',
              'Both parents should have input on major extracurricular decisions',
              'Transportation to activities should be considered in planning'
            ]
          }
        },
        modifications: {
          grounds: [
            'Substantial change in either parent\'s income (typically 15% or more)',
            'Significant change in parent-time schedule or custody arrangement',
            'Change in health insurance costs or availability',
            'Change in childcare expenses or needs',
            'Child\'s needs change significantly (special needs, medical conditions)',
            'Emancipation, marriage, or death of a child',
            'Three years have passed since last order (allows for cost-of-living review)',
            'Change in number of children covered by the order'
          ],
          process: [
            'File Motion to Modify Child Support with supporting documentation',
            'Serve the other parent with motion and updated financial declaration',
            'Provide current income verification and expense documentation',
            'Attend court hearing or mediation as scheduled by court',
            'Present evidence supporting the requested modification',
            'Obtain modified court order with new support amount and effective date'
          ],
          timeline: 'Modifications are typically effective from the date of filing the motion, not the date of the final court order',
          documentation: [
            'Updated Financial Declaration with current income and expenses',
            'Recent pay stubs, tax returns, and employment verification',
            'Documentation of changed circumstances (medical bills, job loss, etc.)',
            'Current childcare and health insurance costs',
            'Evidence of parent-time schedule changes if applicable'
          ]
        },
        enforcement: {
          methods: [
            'Income withholding and automatic wage garnishment',
            'Interception of tax refunds (federal and state)',
            'Asset seizure including bank accounts and personal property',
            'Liens on real estate and personal property',
            'Suspension of driver\'s license and professional licenses',
            'Suspension of hunting, fishing, and recreational licenses',
            'Credit bureau reporting of delinquent support',
            'Contempt of court proceedings with possible jail time',
            'Passport denial for arrearages exceeding $2,500',
            'Interception of lottery winnings and gaming proceeds',
            'Garnishment of unemployment and workers\' compensation benefits'
          ],
          interest: 'Unpaid child support accrues interest at the rate of 12% per annum on past-due amounts',
          statute: 'No statute of limitations exists for collecting past-due child support - it remains collectible indefinitely',
          resources: [
            'Utah Office of Recovery Services',
            'Local prosecuting attorney offices for contempt proceedings',
            'Private collection agencies contracted by the state',
            'Interstate enforcement through other state agencies'
          ]
        }
      }
    },
    parentTimeSchedules: {
      description: 'Utah has detailed statutory parent-time schedules that serve as minimum standards',
      ageGroups: {
        birth_to_5: {
          description: 'Children from birth to 5 years old',
          schedule: [
            'One weekday evening per week for 3 hours',
            'Alternating weekends from Friday 6:00 PM to Sunday 7:00 PM',
            'One-half of school holidays',
            'Two weeks during summer (may be nonconsecutive for children under 3)'
          ],
          considerations: [
            'Nursing mothers - schedule may be adjusted for breastfeeding',
            'Very young children - overnights may be gradually introduced',
            'Consistency and routine are emphasized for young children'
          ]
        },
        age_5_to_18: {
          description: 'Children from 5 to 18 years old',
          schedule: [
            'One weekday evening per week until 8:30 PM (9:00 PM for children 12+)',
            'Alternating weekends from Friday after school until Monday morning (or Sunday 7:00 PM if no school)',
            'One-half of school holidays and teacher development days',
            'Extended summer parent-time for up to 4 weeks (may be consecutive or divided)'
          ],
          considerations: [
            'School-age children - schedule follows school calendar',
            'Teenage children - preferences may be considered but parents retain authority',
            'Extracurricular activities should not interfere with parent-time'
          ]
        }
      },
      holidays: {
        description: 'Utah statute provides specific holiday parent-time schedules',
        evenYears: [
          'Martin Luther King Jr. Day',
          'Spring Break or Easter weekend',
          'Memorial Day weekend',
          'July 24th (Pioneer Day - Utah holiday)',
          'Labor Day weekend',
          'Halloween (6:00 PM to 9:00 PM)',
          'Thanksgiving weekend (Wednesday 7:00 PM to Sunday 7:00 PM)'
        ],
        oddYears: [
          'Presidents\' Day weekend',
          'July 4th weekend',
          'Columbus Day weekend',
          'Christmas school vacation (first half)',
          'New Year\'s Day'
        ],
        annual: [
          'Father\'s Day weekend (to father)',
          'Mother\'s Day weekend (to mother)',
          'Child\'s birthday (until 7:00 PM with 48-hour notice)'
        ]
      },
      summer: {
        description: 'Extended summer parent-time provisions',
        duration: 'Up to 4 consecutive weeks or the same amount of time divided into two separate periods',
        notice: '30 days written notice required, with preference given to first notice received',
        restrictions: [
          'May not interfere with the child\'s summer school if required for advancement',
          'Should not conflict with established family vacation plans',
          'Must coordinate with other parent\'s work schedule when possible'
        ]
      },
      makeUp: {
        description: 'Utah requires make-up parent-time when scheduled time is missed',
        circumstances: [
          'When custodial parent denies or interferes with scheduled parent-time',
          'When parent-time is missed due to custodial parent\'s actions',
          'Court may order specific make-up time arrangements'
        ],
        requirements: [
          'Make-up time should be of equal duration to missed time',
          'Should be scheduled as soon as reasonably possible',
          'Both parents should cooperate in scheduling make-up time'
        ]
      }
    },
    resources: {
      courts: [
        {
          name: 'Utah State Courts - Family Law Resources',
          url: 'https://www.utcourts.gov/howto/family/',
          description: 'Official Utah court system family law division with forms, procedures, and self-help resources'
        },
        {
          name: 'Utah Family Law Forms',
          url: 'https://www.utcourts.gov/resources/forms/family/',
          description: 'Complete collection of Utah family court forms for divorce, custody, and support cases'
        },
        {
          name: 'Utah Self-Help Resources',
          url: 'https://www.utcourts.gov/selfhelp/',
          description: 'Comprehensive self-help resources for people representing themselves in family court'
        },
        {
          name: 'Third District Court (Salt Lake County)',
          address: '450 South State Street, Salt Lake City, UT 84114',
          phone: '(801) 238-7300',
          description: 'Family court serving Salt Lake County including Salt Lake City metropolitan area'
        },
        {
          name: 'Fourth District Court (Utah County)',
          address: '125 North 100 West, Provo, UT 84601',
          phone: '(801) 429-1000',
          description: 'Family court serving Utah County including Provo, Orem, and surrounding areas'
        },
        {
          name: 'First District Court (Northern Utah)',
          address: '1700 North Main Street, Logan, UT 84341',
          phone: '(435) 750-1300',
          description: 'Family court serving Cache, Box Elder, and Rich Counties in northern Utah'
        }
      ],
      support: [
        {
          name: 'Utah Office of Recovery Services',
          url: 'https://jobs.utah.gov/ors/',
          phone: '1-800-662-9651',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Utah Child Support Calculator',
          url: 'https://orscsc.dts.utah.gov/',
          description: 'Official Utah child support calculator for estimating support amounts'
        },
        {
          name: 'Utah Legal Aid Society',
          url: 'https://www.utahlegalaid.org/',
          phone: '(801) 328-8849',
          description: 'Free legal services for low-income families in family law matters statewide'
        },
        {
          name: 'Utah State Bar Family Law Section',
          url: 'https://www.utahbar.org/sections/family-law/',
          description: 'Professional resources and directory of family law attorneys in Utah'
        },
        {
          name: 'Utah Domestic Violence Coalition',
          url: 'https://www.udvc.org/',
          phone: '1-800-897-5465',
          description: 'Statewide domestic violence resources, shelters, and legal advocacy services'
        }
      ],
      legal: [
        {
          name: 'Utah State Bar Lawyer Referral Service',
          url: 'https://www.utahbar.org/public-services/lawyer-referral-service/',
          phone: '(801) 297-7047',
          description: 'Professional attorney referrals for family law cases throughout Utah'
        },
        {
          name: 'University of Utah S.J. Quinney College of Law Legal Clinic',
          url: 'https://law.utah.edu/academics/clinics/',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Utah Collaborative Law Institute',
          url: 'https://www.collaborativelaw.org/find-a-professional/utah/',
          description: 'Network of collaborative divorce attorneys and professionals in Utah'
        },
        {
          name: 'Utah Mediation Association',
          url: 'https://www.utahmediation.org/',
          description: 'Family mediation services and dispute resolution professionals statewide'
        }
      ],
      educational: [
        {
          name: 'Utah Parent Education Program',
          url: 'https://www.utcourts.gov/howto/family/parent_ed/',
          description: 'Mandatory parent education program for divorcing parents with minor children'
        },
        {
          name: 'Utah Parenting Plan Resources',
          url: 'https://www.utcourts.gov/howto/family/parentingplan/',
          description: 'Comprehensive guide to creating effective parenting plans in Utah'
        },
        {
          name: 'Utah Guardian ad Litem Program',
          url: 'https://www.utcourts.gov/courts/gal/',
          description: 'Information about guardian ad litem services for children in contested custody cases'
        },
        {
          name: 'Utah State University Extension - Family Life',
          url: 'https://extension.usu.edu/relationships/',
          description: 'Educational programs for co-parenting, family relationships, and child development'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'Third District Court (Salt Lake County)',
            address: '450 South State Street, Salt Lake City, UT 84114',
            phone: '(801) 238-7300',
            jurisdiction: 'Salt Lake County - Salt Lake City, West Valley City, Murray, and surrounding areas'
          },
          {
            name: 'Fourth District Court (Utah County)',
            address: '125 North 100 West, Provo, UT 84601',
            phone: '(801) 429-1000',
            jurisdiction: 'Utah County - Provo, Orem, Spanish Fork, and surrounding areas'
          },
          {
            name: 'Second District Court (Weber County)',
            address: '2525 Grant Avenue, Ogden, UT 84401',
            phone: '(801) 395-1000',
            jurisdiction: 'Weber County - Ogden, Roy, Clearfield, and northern Utah areas'
          },
          {
            name: 'First District Court (Cache County)',
            address: '1700 North Main Street, Logan, UT 84341',
            phone: '(435) 750-1300',
            jurisdiction: 'Cache County - Logan and surrounding northern Utah areas'
          },
          {
            name: 'Fifth District Court (Washington County)',
            address: '220 North 200 East, St. George, UT 84770',
            phone: '(435) 986-5700',
            jurisdiction: 'Washington County - St. George and southern Utah areas'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$318.00',
          'Divorce (without children)': '$318.00',
          'Custody/Parent-time': '$225.00',
          'Child Support': '$225.00',
          'Modification': '$225.00',
          'Paternity': '$225.00',
          'Protective Order': 'No fee'
        },
        requiredForms: [
          'Petition for Divorce or Custody',
          'Summons',
          'Financial Declaration',
          'Parenting Plan (if children involved)',
          'Child Support Obligation Worksheet',
          'Certificate of Service'
        ]
      },
      statutes: [
        {
          name: 'Utah Code Title 30 - Husband and Wife (Divorce and Custody)',
          url: 'https://le.utah.gov/xcode/Title30/Chapter3/30-3.html',
          description: 'Primary statutes governing divorce, custody, and family law in Utah'
        },
        {
          name: 'Utah Child Support Guidelines (Utah Code § 78B-12-301 et seq.)',
          url: 'https://le.utah.gov/xcode/Title78B/Chapter12/78B-12.html',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'Utah Parent-Time Act (Utah Code § 30-3-35 et seq.)',
          url: 'https://le.utah.gov/xcode/Title30/Chapter3/30-3-S35.html',
          description: 'Comprehensive parent-time schedules and requirements for Utah'
        }
      ],
      emergencyContacts: [
        {
          name: 'Utah Domestic Violence Hotline',
          phone: '1-800-897-5465',
          description: '24/7 statewide domestic violence crisis line and support services'
        },
        {
          name: 'Utah Child Abuse Hotline',
          phone: '1-855-323-3237',
          description: '24/7 child abuse and neglect reporting hotline'
        },
        {
          name: 'Utah Crisis Line',
          phone: '1-800-273-8255',
          description: '24/7 mental health crisis intervention and counseling referrals'
        }
      ]
    },
    bestInterestsFactors: [
      'Whether the physical, psychological, and emotional needs and development of the child will be met',
      'The capability, desire, and willingness of each of the parties to meet the needs of the child',
      'The length of time the child has lived in a stable, satisfactory environment and the desirability of maintaining continuity',
      'The stability of the family unit of the parties and the relative strength of the child\'s bond with one or both of the prospective custodians',
      'The preference of the child if the child is of sufficient age and capacity to reason so as to form an intelligent preference as to custody',
      'The general interest in continuing previously determined custody arrangements where the child has been happy and well-adjusted',
      'Factors relating to the prospective custodians\' character, status, and lifestyle that bear on the welfare and happiness of the child',
      'The willingness and demonstrated ability of each party to facilitate and encourage a close and continuing parent-child relationship between the child and the other party',
      'Those factors that a court determines relevant to the particular custody dispute before the court'
    ],
    practicalTips: [
      'Utah courts strictly follow statutory parent-time schedules as minimum standards - be familiar with them',
      'Document all parent-time exchanges and any violations of the court order',
      'Complete the mandatory parent education class within 30 days of filing',
      'Utah courts favor parents who demonstrate willingness to facilitate the child\'s relationship with the other parent',
      'Keep detailed records of expenses related to the children for potential support modifications',
      'Follow Utah\'s specific relocation procedures if planning to move more than 150 miles',
      'Consider Utah\'s unique cultural factors when developing parenting plans',
      'Be prepared to show how your proposed arrangement serves the child\'s best interests'
    ]
  }
};

export default utahData;